import { FilePathField } from 'components/FilePathField/FilePathField'
import { useLocationPickerProps } from 'components/LocationPicker/hooks/useLocationPicker'
import { useField, useFormikContext } from 'formik'
import { TextInput } from '../Wizard/Form/TextInput'
import { useDefaultFolder } from 'hooks/queries/projects/useDefaultFolder'
import { useLastUsedFolder } from 'hooks/queries/projects/useLastUsedFolder'
import { SelectedFolder } from 'components/LocationPicker/LocationPicker'
import Styles from '../Form/Form.module.scss'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { FormStatus } from 'components/Wizards/Wizard/Form/Types'
import { InlineLocationPickerProps } from 'components/InlineLocationPicker/InlineLocationPicker'
import FilePathStyles from 'components/FilePathField/FilePathField.module.scss'
import cx from 'classnames'
import { ButtonVariant } from 'blaze-ui/Button/Types'
import { TextFieldLoadingState } from '../TextFieldLoadingState/TextFieldLoadingState'
import { forwardRef, useEffect } from 'react'
import { InputVariant } from 'almanac-ui/TextInputField/Types'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'

interface Props {
  placeholder?: string
  inputClassName?: string
  locationPickerVariant?: ButtonVariant
  locationPickerFullWidth?: boolean
  isNameLoading?: boolean
  triggerComponent?: 'button' | 'formButton'
  inputVariant?: InputVariant
}

const LocationPicker = (props: InlineLocationPickerProps) => {
  const { status } = useFormikContext()

  if (status === FormStatus.AutoFilling) {
    return <TextFieldLoadingState fixedWidth={250} />
  }

  return <FilePathField.LocationPicker {...props} />
}

const FilePathFormikField = forwardRef<HTMLInputElement, Props>(
  (
    {
      placeholder = 'Name your doc...',
      inputClassName,
      locationPickerVariant,
      locationPickerFullWidth,
      isNameLoading = false,
      triggerComponent,
      inputVariant,
    },
    ref,
  ) => {
    const defaultFolder = useDefaultFolder()
    const { currentFolder } = useCurrentFolderStore()
    const { lastUsedFolder, isLoading } = useLastUsedFolder()
    const [projectField, projectMeta, projectHelpers] = useField('project')
    const [, filenameMeta] = useField('filename')
    const { submitCount } = useFormikContext()

    let folder = projectField.value || currentFolder || lastUsedFolder || defaultFolder

    if (folder?.uploads_project) {
      folder = defaultFolder
    }

    const locationPickerProps = useLocationPickerProps({
      folder,
      onSelectedFolder: projectHelpers.setValue,
    })

    const selectedFolder = currentFolder || lastUsedFolder || defaultFolder

    useEffect(() => {
      if (selectedFolder && !isLoading && !projectField.value) {
        projectHelpers.setValue(selectedFolder)
        locationPickerProps.locationPickerProps.onSelectFolder({
          selectedFolder: selectedFolder as unknown as SelectedFolder,
        })
      }
    }, [selectedFolder, isLoading, projectField.value, projectHelpers, locationPickerProps])

    const showFilenameError = (filenameMeta.touched || submitCount > 0) && !!filenameMeta.error
    const showProjectError = (projectMeta.touched || submitCount > 0) && !!projectMeta.error

    return (
      <div>
        <FilePathField.Root>
          <div ref={ref} className={Styles.fileNameInput}>
            {isNameLoading ? (
              <TextFieldLoadingState />
            ) : (
              <TextInput
                aria-label="filename"
                name="filename"
                placeholder={placeholder}
                isTextarea={false}
                className={cx(FilePathStyles.input, inputClassName, { [Styles.errorInput]: showFilenameError })}
                variant={inputVariant}
              />
            )}
          </div>

          {isLoading ? (
            <div className={Styles.loader}>
              <TextFieldLoadingState />
            </div>
          ) : (
            <LocationPicker
              {...locationPickerProps}
              variant={locationPickerVariant}
              fullWidth={locationPickerFullWidth}
              triggerComponent={triggerComponent}
              size="lg"
            />
          )}
        </FilePathField.Root>

        {showFilenameError && <FormErrorMessage>{filenameMeta.error}</FormErrorMessage>}
        {showProjectError && <FormErrorMessage>{projectMeta.error}</FormErrorMessage>}
      </div>
    )
  },
)

export { FilePathFormikField }
